import _extends from "@babel/runtime-corejs3/helpers/extends";
import React, { useContext } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { intlMessageShape } from '@jutro/prop-types';
import { TranslatorContext } from '@jutro/locale';
import { IconButton } from '../../IconButton/IconButton';
import { Icon } from '../../Icon/Icon';
import { layoutShape } from '../../../layouts/Layout';
import { getLayoutComponent } from '../helpers/modalHelpers';
import styles from "./ModalHeader.module.css";
const defaultContentLayout = {
  component: 'flex',
  componentProps: {
    alignItems: 'top',
    wrap: false,
    gap: 'small'
  }
};
const defaultTitleLayout = {
  component: 'flex',
  componentProps: {
    direction: 'column',
    gap: 'none'
  }
};
export const modalClassMap = {
  warning: {
    status: 'modalWarningStatus',
    icon: 'iconWarning'
  },
  error: {
    status: 'modalErrorStatus',
    icon: 'iconError'
  },
  info: {
    status: 'modalInfoStatus',
    icon: 'iconInfo'
  },
  success: {
    status: 'modalSuccessStatus',
    icon: 'iconSuccess'
  }
};
const modalHeaderPropTypes = {
  contentLayout: PropTypes.oneOfType([PropTypes.shape({
    component: PropTypes.string.isRequired,
    componentProps: PropTypes.object
  }), layoutShape]),
  titleLayout: PropTypes.oneOfType([PropTypes.shape({
    component: PropTypes.string.isRequired,
    componentProps: PropTypes.object
  }), layoutShape]),
  status: PropTypes.oneOf(['success', 'info', 'warning', 'error']),
  icon: PropTypes.string,
  title: intlMessageShape,
  subtitle: intlMessageShape,
  onClose: PropTypes.func,
  titleId: PropTypes.string
};
export const ModalHeader = ({
  contentLayout,
  titleLayout,
  status,
  icon,
  title,
  subtitle,
  onClose,
  titleId,
  ariaLabel
}) => {
  const Layout = getLayoutComponent(contentLayout.component);
  const TitleLayout = getLayoutComponent(titleLayout.component);
  const translator = useContext(TranslatorContext);
  const modalClass = modalClassMap[status] || {};
  const modalStatusClasses = cx(styles.modalStatus, styles[modalClass.status]);
  const iconClasses = cx(styles.icon, styles[modalClass.icon]);
  return React.createElement("div", {
    className: styles.modalHeader
  }, React.createElement("div", {
    className: modalStatusClasses
  }), React.createElement(Layout, _extends({
    className: styles.modalHeaderLayout
  }, contentLayout.componentProps), icon && React.createElement(Icon, {
    icon: icon,
    className: iconClasses
  }), React.createElement(TitleLayout, _extends({
    id: titleId,
    className: styles.modalHeaderTitleLayout
  }, titleLayout.componentProps), title && React.createElement("span", {
    className: styles.title
  }, translator(title)), subtitle && React.createElement("span", {
    className: styles.subtitle
  }, translator(subtitle))), onClose && React.createElement(IconButton, {
    size: "small",
    icon: "mi-close",
    onClick: onClose,
    ariaLabel: ariaLabel,
    className: styles.closeButton
  })));
};
ModalHeader.propTypes = modalHeaderPropTypes;
ModalHeader.defaultProps = {
  contentLayout: defaultContentLayout,
  titleLayout: defaultTitleLayout
};
ModalHeader.__docgenInfo = {
  componentName: "ModalHeader",
  packageName: "@jutro/components",
  description: "ModalHeader",
  displayName: "ModalHeader",
  methods: [],
  actualName: "ModalHeader",
  props: {
    contentLayout: {
      type: {
        name: "union",
        value: [{
          name: "shape",
          value: {
            component: {
              name: "string",
              required: true
            },
            componentProps: {
              name: "object",
              required: false
            }
          }
        }, {
          name: "shape",
          value: {
            id: {
              name: "string",
              description: "Used to identify the component",
              required: false
            },
            repeat: {
              name: "union",
              value: [{
                name: "enum",
                value: [{
                  value: "'auto-fit'",
                  computed: false
                }, {
                  value: "'auto-fill'",
                  computed: false
                }]
              }, {
                name: "number"
              }, {
                name: "string"
              }],
              description: "repeat columns: 'auto-fit' or 'auto-fill' or number",
              required: false
            },
            gap: {
              name: "enum",
              computed: true,
              value: "Object.keys(gaps)",
              description: "gap between rows and columns: 'none', 'small', 'medium', 'large'",
              required: false
            },
            columns: {
              name: "array",
              description: "define explicit columns",
              required: false
            },
            colSpan: {
              name: "union",
              value: [{
                name: "number"
              }, {
                name: "string"
              }],
              required: false
            },
            colStart: {
              name: "union",
              value: [{
                name: "number"
              }, {
                name: "string"
              }],
              required: false
            }
          }
        }]
      },
      required: false,
      description: "defines the content layout to be used with a 'component' property set to either Flex or Grid and\n'componentProps' to set properties for that layout component",
      defaultValue: {
        value: "{\n    component: 'flex',\n    componentProps: {\n        alignItems: 'top',\n        wrap: false,\n        gap: 'small',\n    },\n}",
        computed: false
      }
    },
    titleLayout: {
      type: {
        name: "union",
        value: [{
          name: "shape",
          value: {
            component: {
              name: "string",
              required: true
            },
            componentProps: {
              name: "object",
              required: false
            }
          }
        }, {
          name: "shape",
          value: {
            id: {
              name: "string",
              description: "Used to identify the component",
              required: false
            },
            repeat: {
              name: "union",
              value: [{
                name: "enum",
                value: [{
                  value: "'auto-fit'",
                  computed: false
                }, {
                  value: "'auto-fill'",
                  computed: false
                }]
              }, {
                name: "number"
              }, {
                name: "string"
              }],
              description: "repeat columns: 'auto-fit' or 'auto-fill' or number",
              required: false
            },
            gap: {
              name: "enum",
              computed: true,
              value: "Object.keys(gaps)",
              description: "gap between rows and columns: 'none', 'small', 'medium', 'large'",
              required: false
            },
            columns: {
              name: "array",
              description: "define explicit columns",
              required: false
            },
            colSpan: {
              name: "union",
              value: [{
                name: "number"
              }, {
                name: "string"
              }],
              required: false
            },
            colStart: {
              name: "union",
              value: [{
                name: "number"
              }, {
                name: "string"
              }],
              required: false
            }
          }
        }]
      },
      required: false,
      description: "defines the main title layout to be used with a 'component' property set to either Flex or Grid and\n'componentProps' to set properties for that layout component",
      defaultValue: {
        value: "{\n    component: 'flex',\n    componentProps: {\n        direction: 'column',\n        gap: 'none',\n    },\n}",
        computed: false
      }
    },
    status: {
      type: {
        name: "enum",
        value: [{
          value: "'success'",
          computed: false
        }, {
          value: "'info'",
          computed: false
        }, {
          value: "'warning'",
          computed: false
        }, {
          value: "'error'",
          computed: false
        }]
      },
      required: false,
      description: "The status of this modal.  Either a 'success', 'info', 'warning', error'.  Default to no status"
    },
    icon: {
      type: {
        name: "string"
      },
      required: false,
      description: "Material Icon name (mi-*)"
    },
    title: {
      type: {
        name: "union",
        value: [{
          name: "string"
        }, {
          name: "shape",
          value: {
            id: {
              name: "string",
              required: false
            },
            defaultMessage: {
              name: "string",
              required: false
            },
            args: {
              name: "shape",
              value: {},
              required: false
            }
          }
        }]
      },
      required: false,
      description: "Text to display for the title"
    },
    subtitle: {
      type: {
        name: "union",
        value: [{
          name: "string"
        }, {
          name: "shape",
          value: {
            id: {
              name: "string",
              required: false
            },
            defaultMessage: {
              name: "string",
              required: false
            },
            args: {
              name: "shape",
              value: {},
              required: false
            }
          }
        }]
      },
      required: false,
      description: "Text to display for the subtitle"
    },
    onClose: {
      type: {
        name: "func"
      },
      required: false,
      description: "The function to be closed when the close button is clicked"
    },
    titleId: {
      type: {
        name: "string"
      },
      required: false,
      description: "Used to identify modal header title content."
    }
  }
};